/**
 * Paintly Wishlist
 *
 * Stores product handles, syncs the hearts, the header badge and the wishlist page,
 * and mirrors the list onto the Klaviyo profile.
 *
 * REWRITTEN 12 Aug 2026. What changed and why:
 *
 * 1. It no longer downloads `/products/<handle>.json`. That endpoint returns the whole
 *    product, which on these kits is about 160 KB, and the Klaviyo sync pulled one per
 *    saved item on EVERY page of the site. A seven item wishlist therefore cost roughly
 *    1.1 MB of JSON per page view, and the wishlist page itself took 22 seconds to draw
 *    seven cards. Everything now comes from `sections/pw-card.liquid`, about 2.5 KB,
 *    which returns the real product card AND the few fields Klaviyo needs.
 * 2. The Klaviyo profile sync only runs when the list has actually changed. Before, it
 *    ran on every page load regardless.
 * 3. The wishlist page renders the site's own editorial card instead of a bespoke one,
 *    so the craft pill, review row, Anton price, image treatment and currency are all
 *    the theme's rather than reimplemented here.
 * 4. Add to Cart is gone. It added `variants[0]`, which on a kit with 21 sizes and six
 *    colour tiers is the 20x20cm 16 colour combination, so a customer who saved a large
 *    120 colour design got a $19.95 small one in their cart. The card now links to the
 *    product page to choose options.
 * 5. Removing an item patches that one row out instead of re-rendering the whole grid.
 *
 * Cross device sync: `remote` below is a thin adapter. It stays dormant until
 * `window.PAINTLY_WISHLIST_SYNC` is set to an app proxy path, at which point the list
 * is read from and written to the signed in customer's account.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'paintly_wishlist';
  var SYNCED_KEY = 'paintly_wishlist_synced';   /* signature last sent to Klaviyo */
  var META_KEY = 'paintly_wishlist_meta';       /* per tab cache of card metadata */
  var CARD_SECTION = 'pw-card';
  var GONE = {};                                /* a real 404, told apart from every other failure */
  var toastTimer = null;

  /* ── list ── */
  function getList() {
    try {
      var l = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      /* defend against duplicates and junk from older versions */
      var seen = {}, out = [];
      for (var i = 0; i < l.length; i++) {
        var h = l[i];
        if (typeof h === 'string' && h && !seen[h]) { seen[h] = 1; out.push(h); }
      }
      return out;
    } catch (e) { return []; }
  }

  function saveList(list) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (e) {}
    remote.push(list);
    document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { list: list } }));
  }

  function sig(list) { return list.join('|'); }

  /* ── metadata cache ──
     One small fetch per product, reused by the page, the toast and Klaviyo. */
  function readMetaCache() {
    try { return JSON.parse(sessionStorage.getItem(META_KEY)) || {}; } catch (e) { return {}; }
  }
  function writeMetaCache(m) {
    try { sessionStorage.setItem(META_KEY, JSON.stringify(m)); } catch (e) {}
  }

  /* One request per handle, ever. The page render and the Klaviyo sync both want the
     same card, and without this they raced and fetched four of the seven twice. */
  var inflight = {};

  function fetchCard(handle) {
    if (!inflight[handle]) {
      inflight[handle] = _fetchCard(handle).then(function (r) {
        /* keep the promise only while it is useful: a resolved node can be adopted
           into the DOM exactly once, so a later caller must fetch its own */
        delete inflight[handle];
        return r;
      });
    }
    return inflight[handle];
  }

  /* Resolves to { meta, node } where node is the <li> for the grid, or null if the
     product no longer exists on the storefront. */
  function _fetchCard(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '?section_id=' + CARD_SECTION,
                 { headers: { 'Accept': 'text/html' } })
      /* Only a 404 means the product is gone. Anything else that is not a card is a hiccup, not
         an answer: a busy shop (429), a server error, an empty answer, or a 200 page that is not
         the card at all, such as an error or bot-check page. Those keep the saved kit and try again
         next visit, because prune below forgets on the account as well as on this device, so a
         wrong "gone" costs somebody a saved kit for good (Codex review, 21 Oct 2026). */
      .then(function (r) { return r.status === 404 ? GONE : (r.ok ? r.text() : null); })
      .then(function (html) {
        if (html === GONE) return { meta: null, node: null, gone: true };
        if (!html) return { meta: null, node: null, gone: false };
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('.pw-item');
        if (!node) return { meta: null, node: null, gone: false };
        var meta = null;
        var tag = node.querySelector('script[data-pw-meta]');
        if (tag) { try { meta = JSON.parse(tag.textContent); } catch (e) {} }
        if (tag) tag.remove();
        if (meta) {
          var cache = readMetaCache();
          cache[handle] = meta;
          writeMetaCache(cache);
        }
        return { meta: meta, node: node, gone: false };
      })
      .catch(function () { return { meta: null, node: null, gone: false }; });
  }

  /* Metadata only, straight from cache when we already have it. */
  function metaFor(handle) {
    var cache = readMetaCache();
    if (cache[handle]) return Promise.resolve(cache[handle]);
    return fetchCard(handle).then(function (r) { return r.meta; });
  }

  /* Run promises a few at a time. Twenty saved designs firing at once is twenty
     section renders, which is rude to the shop and slower than a small queue. */
  function pooled(items, worker, limit) {
    limit = limit || 6;
    var out = new Array(items.length), i = 0;
    function next() {
      if (i >= items.length) return Promise.resolve();
      var at = i++;
      return Promise.resolve(worker(items[at], at)).then(function (v) {
        out[at] = v;
        return next();
      });
    }
    var runners = [];
    for (var k = 0; k < Math.min(limit, items.length); k++) runners.push(next());
    return Promise.all(runners).then(function () { return out; });
  }

  /* ── cross device sync (dormant until a proxy path is configured) ── */
  var remote = {
    url: function () { return window.PAINTLY_WISHLIST_SYNC || null; },
    /* Read the account copy and merge it with this device's copy. A union, never a
       replace: whichever device saved something last, nothing is silently dropped. */
    pull: function () {
      var u = this.url();
      if (!u) return Promise.resolve(null);
      return fetch(u, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (!d || !Array.isArray(d.wishlist)) return null;
          var local = getList(), merged = local.slice(), seen = {};
          local.forEach(function (h) { seen[h] = 1; });
          d.wishlist.forEach(function (h) { if (typeof h === 'string' && h && !seen[h]) { seen[h] = 1; merged.push(h); } });
          if (sig(merged) !== sig(local)) {
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch (e) {}
            document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { list: merged } }));
          }
          if (sig(merged) !== sig(d.wishlist)) remote.push(merged);
          return d;
        })
        .catch(function () { return null; });
    },
    push: function (list) {
      var u = this.url();
      if (!u) return;
      clearTimeout(remote._t);
      remote._t = setTimeout(function () {
        fetch(u, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ wishlist: list })
        }).catch(function () {});
      }, 400);
    }
  };

  /* ── Klaviyo ── */
  function trackKlaviyoEvent(eventName, handle) {
    metaFor(handle).then(function (m) {
      if (!m) return;
      (window._learnq = window._learnq || []).push(['track', eventName, {
        ProductName: m.title, ProductID: m.id, Handle: m.handle,
        Categories: m.type ? [m.type] : [], ImageURL: m.image,
        URL: window.location.origin + m.url, Price: m.price,
        CompareAtPrice: m.compare || 0, Tags: m.tags || [],
        WishlistCount: getList().length
      }]);
    });
  }

  var _syncTimer = null;
  function syncWishlistToKlaviyo(force) {
    var handles = getList();
    if (!force && sig(handles) === localStorage.getItem(SYNCED_KEY)) return;
    clearTimeout(_syncTimer);
    _syncTimer = setTimeout(function () { _doSyncWishlist(handles); }, 500);
  }

  function _doSyncWishlist(handles) {
    var _learnq = window._learnq = window._learnq || [];
    function done(items) {
      _learnq.push(['identify', {
        WishlistItems: items,
        WishlistCount: items.length,
        WishlistLastUpdated: new Date().toISOString()
      }]);
      try { localStorage.setItem(SYNCED_KEY, sig(handles)); } catch (e) {}
    }
    if (!handles.length) return done([]);
    pooled(handles, metaFor, 4).then(function (metas) {
      done(metas.filter(Boolean).map(function (m) {
        return {
          ProductName: m.title, ProductID: m.id, Handle: m.handle,
          Categories: m.type ? [m.type] : [], ImageURL: m.image,
          URL: window.location.origin + m.url, Price: m.price,
          CompareAtPrice: m.compare || 0
        };
      }));
    }).catch(function () {});
  }

  /* ── toast ── */
  function showToast(message, icon) {
    var existing = document.querySelector('.pw-toast');
    if (existing) existing.remove();
    clearTimeout(toastTimer);
    var toast = document.createElement('div');
    toast.className = 'pw-toast';
    var HEART = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';
    if (icon === 'added') {
      toast.innerHTML = HEART.replace('<svg ', '<svg fill="#ef4444" stroke="#ef4444" stroke-width="1" ') + message;
    } else if (icon === 'removed') {
      toast.innerHTML = HEART.replace('<svg ', '<svg fill="none" stroke="#CEA3D9" stroke-width="2" ') + message;
    } else {
      toast.textContent = message;
    }
    document.body.appendChild(toast);
    toast.offsetHeight;
    toast.classList.add('pw-toast--visible');
    toastTimer = setTimeout(function () {
      toast.classList.remove('pw-toast--visible');
      setTimeout(function () { toast.remove(); }, 400);
    }, 2200);
  }

  /* ── public API ── */
  window.PaintlyWishlist = {
    has: function (handle) { return getList().indexOf(handle) > -1; },
    toggle: function (handle) {
      var list = getList(), idx = list.indexOf(handle);
      if (idx > -1) {
        list.splice(idx, 1);
        saveList(list);
        showToast('Removed from wishlist', 'removed');
        trackKlaviyoEvent('Removed from Wishlist', handle);
        syncWishlistToKlaviyo();
        return false;
      }
      list.push(handle);
      saveList(list);
      showToast('Added to wishlist', 'added');
      trackKlaviyoEvent('Added to Wishlist', handle);
      syncWishlistToKlaviyo();
      return true;
    },
    remove: function (handle) {
      saveList(getList().filter(function (h) { return h !== handle; }));
      showToast('Removed from wishlist', 'removed');
      trackKlaviyoEvent('Removed from Wishlist', handle);
      syncWishlistToKlaviyo();
    },
    getAll: function () { return getList(); },
    /* for the account page: a kit it found gone from the shop, forgotten the same way as here */
    prune: function (handle) { if (typeof handle === 'string' && handle) prune(handle); },
    count: function () { return getList().length; }
  };

  /* ── hearts and badge ── */
  function syncHearts() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      var handle = btn.dataset.productHandle;
      if (!handle) return;
      var active = PaintlyWishlist.has(handle);
      btn.classList.toggle('pw-heart--active', active);
      btn.setAttribute('aria-pressed', active);
      btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    });
  }

  function syncBadge() {
    var count = PaintlyWishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      var badge = el.closest('.pw-header-badge');
      if (!badge) return;
      badge.classList.toggle('pw-header-badge--hidden', count === 0);
      if (count > 0) {
        badge.classList.remove('pw-header-badge--pop');
        badge.offsetHeight;
        badge.classList.add('pw-header-badge--pop');
      }
    });
  }

  /* ── wishlist page ── */
  function page() { return document.querySelector('.pw-page'); }

  function setChrome(n) {
    var count = document.getElementById('pw-count');
    var empty = document.getElementById('pw-wishlist-empty');
    var grid = document.getElementById('pw-wishlist-grid');
    var head = document.querySelector('.pw-sechead');
    if (count) { count.textContent = n; count.hidden = n === 0; }
    if (empty) empty.hidden = n !== 0;
    if (head) head.hidden = n === 0;
    if (grid) grid.hidden = n === 0;
  }

  function setWhere() {
    var el = document.getElementById('pw-where');
    var txt = document.getElementById('pw-where-text');
    var p = page();
    if (!el || !txt || !p) return;
    if (p.dataset.signedIn === '1') {
      txt.textContent = 'Saved to your account, so it follows you to any device.';
    } else {
      var url = p.dataset.loginUrl || '/account';
      txt.innerHTML = 'Saved on this device only. <a href="' + url +
        '">Sign in</a> and your list follows you to your phone and laptop.';
    }
    el.hidden = false;
  }

  /* A change that lands while the grid is still being drawn (the account copy merging in kits saved on
     another device, most often) is drawn as soon as that finishes, not dropped: it had left the page
     showing fewer kits than the header badge counted (Codex review, 11 Oct 2026). */
  var rendering = false, again = false;
  function drawn() { if (again) { again = false; renderWishlistPage(); } }
  /* settles with the account copy once init has read and merged it (null when there is none) */
  var pullDone, pulled = new Promise(function (res) { pullDone = res; });

  function renderWishlistPage() {
    var grid = document.getElementById('pw-wishlist-grid');
    if (!grid) return;
    if (rendering) { again = true; return; }
    var handles = getList().slice().reverse();          /* newest saved first */
    var loading = document.getElementById('pw-wishlist-loading');

    /* already drawn: patch rather than rebuild */
    if (grid.children.length) {
      var have = {};
      [].slice.call(grid.children).forEach(function (li) {
        var h = li.dataset.handle;
        have[h] = li;
        if (handles.indexOf(h) < 0) {
          li.classList.add('pw-item--removing');
          setTimeout(function () {
            li.remove();
            setChrome(grid.children.length);
          }, 360);
        }
      });
      var missing = handles.filter(function (h) { return !have[h]; });
      if (!missing.length) { setChrome(handles.length); return; }
      rendering = true;
      pooled(missing, fetchCard, 6).then(function (res) {
        res.forEach(function (r, i) {
          if (r && r.node) grid.insertBefore(r.node, grid.firstChild);
          else if (r && r.gone) prune(missing[i]);
        });
        rendering = false;
        setChrome(grid.children.length);
        syncHearts();
        drawn();
      });
      return;
    }

    setChrome(handles.length);
    if (!handles.length) { if (loading) loading.hidden = true; return; }

    rendering = true;
    if (loading) loading.hidden = false;
    pooled(handles, fetchCard, 6).then(function (res) {
      if (loading) loading.hidden = true;
      var gone = [];
      res.forEach(function (r, i) {
        if (r && r.node) grid.appendChild(r.node);
        else if (r && r.gone) gone.push(handles[i]);
      });
      rendering = false;
      /* a design that has been deleted or unpublished should not sit in the list
         for ever, invisible and uncountable */
      if (gone.length) gone.forEach(prune);
      setChrome(grid.children.length);
      syncHearts();
      drawn();
    });
  }

  /* Gone from the shop, so gone from the list: this device's copy AND the account's. Pruning only
     the device left the account copy holding it, the next page load merged it straight back, and
     the header badge counted a kit no page could show (Codex review, 11 Oct 2026).
     The account copy is only written once this page has merged it in (remote.pull): writing this
     device's list before that would replace the account's list and drop kits saved on another
     device. If the account copy could not be read, only this device forgets it. */
  function dropLocal(handle) {
    var list = getList();
    if (list.indexOf(handle) < 0) return null;
    list = list.filter(function (h) { return h !== handle; });
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (e) {}
    return list;
  }
  function prune(handle) {
    dropLocal(handle);
    syncBadge();
    pulled.then(function (d) {
      if (!d) return;
      dropLocal(handle);                    /* the merge may have brought it back */
      remote.push(getList());               /* this device now holds the union, less the gone kit */
      syncBadge();
    });
  }

  /* ── delegated handlers ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var handle = btn.dataset.productHandle;
    if (!handle) return;
    btn.classList.remove('pw-heart--bounce');
    btn.offsetHeight;
    btn.classList.add('pw-heart--bounce');
    PaintlyWishlist.toggle(handle);
  });

  document.addEventListener('wishlist:changed', function () {
    syncHearts();
    syncBadge();
    renderWishlistPage();
  });

  function init() {
    syncHearts();
    syncBadge();
    if (page()) { setWhere(); renderWishlistPage(); }
    /* only talks to the network when the list has actually moved */
    syncWishlistToKlaviyo();
    pullDone(remote.pull());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    setTimeout(function () { syncHearts(); syncBadge(); }, 100);
  });
})();
